import type { Unit, ValueType } from 'chery-contracts';
import { roundToOneDecimal } from 'chery-kpi-engine';

/**
 * CSV rules shared by every export (Tracker, Reports): UTF-8 with a BOM and CRLF line ends so Excel opens the file
 * as is; numbers are plain at display precision (`1450.0`) so they stay numbers in a sheet; a text cell starting
 * with `= + - @` is neutralised so a spreadsheet never runs it as a formula.
 */

export const CSV_BOM = '﻿';
export const CSV_EOL = '\r\n';

export const UNIT_LABELS: Readonly<Record<Unit, string>> = {
  NUMBER: 'Number',
  PERCENTAGE: 'Percentage',
  CURRENCY: 'Currency',
  DAYS: 'Days',
  HOURS: 'Hours',
  RATIO: 'Ratio',
  DECIMAL: 'Decimal',
  TEXT: 'Text',
};

/** The value-type labels (decision 22c): what one week's number means, so the Alcaraz inversion cannot recur. Same as `enums.valueType`. */
export const VALUE_TYPE_LABELS: Readonly<Record<ValueType, string>> = { ABSOLUTE: 'Weekly amount — adds up', CUMULATIVE: 'Balance at week end' };

/** A text cell a spreadsheet would read as a formula gets a leading apostrophe. */
export const neutraliseFormula = (text: string): string => (/^[=+\-@\t\r]/.test(text) ? `'${text}` : text);

/** Quotes a field when it holds a comma, a quote or a line break. */
export const csvField = (field: string): string => (/[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field);

/** A user-typed text (a name, a Text KPI's value): neutralised, then quoted when needed. */
export const csvText = (text: string): string => csvField(neutraliseFormula(text));

/** A number at display precision, plain: `1450.0`, `-3.0`; an empty cell for a blank — never 0. */
export const csvNumber = (value: number | null): string => (value === null || !Number.isFinite(value) ? '' : roundToOneDecimal(value).toFixed(1));

/** One CSV document: BOM, the rows joined with commas, CRLF after every line. */
export const csvDocument = (rows: readonly (readonly string[])[]): string => CSV_BOM + rows.map((row) => row.join(',')).join(CSV_EOL) + CSV_EOL;

/** Hands a text file to the browser as a download. */
export const downloadText = (fileName: string, content: string, type = 'text/csv;charset=utf-8'): void => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
